/**
 * @fileoverview Captcha-strings barrel + the `captcha` namespace registrar.
 * @package @puffin-core/captcha-ui
 *
 * AAS-I18N-002 W8 / user ruling 25 (2026-08-10). Mirrors
 * `@puffin-core/login-shell`'s `loadLoginStrings` and `@puffin-core/page-shell`'s
 * `loadShellStrings`.
 *
 * ## Why this package ALSO self-registers, when its three siblings do not
 *
 * The sibling loaders are wired by hand in **20** `createAppI18n` call sites
 * (10 apps × app + marketing portal). That fan-out is exactly the failure the
 * RECIPE names — *"a binding seam that nobody mounts does not exist"*: the
 * `login` namespace's own predecessor sat with the right shape and zero callers
 * for eight days. `<CaptchaField>` has **91 render sites** across apps and
 * packages, several of them outside any login flow (password-reset modals,
 * public contact forms, the signup wizard), so an app can legitimately render it
 * without ever thinking about login-shell.
 *
 * So `useCaptchaTranslate` calls `ensureCaptchaStrings(i18n)` on the instance it
 * finds in the React tree. That call is **idempotent and non-overwriting** — it
 * checks `hasResourceBundle` first and passes `overwrite=false` — so an app that
 * has injected its own `captcha.*` override keeps it, and a second component
 * mounting is a no-op.
 *
 * `loadCaptchaStrings` is exported anyway, for an app that prefers to preload
 * explicitly at bootstrap like it does for the other three bundles. Both paths
 * converge on the same bundles.
 */

import { DEFAULT_CAPTCHA_STRINGS } from './en.js';
import { CAPTCHA_STRINGS_ES } from './es.js';
import { CAPTCHA_NAMESPACE } from './namespace.js';

/**
 * The English bundle under a name symmetric with `CAPTCHA_STRINGS_ES`. Same
 * frozen object as `DEFAULT_CAPTCHA_STRINGS`, which keeps its own export because
 * the fallback path imports it by that name.
 */
export const CAPTCHA_STRINGS_EN = DEFAULT_CAPTCHA_STRINGS;

export { CAPTCHA_STRINGS_ES, DEFAULT_CAPTCHA_STRINGS, CAPTCHA_NAMESPACE };

const BUNDLES = Object.freeze({
  en: CAPTCHA_STRINGS_EN,
  es: CAPTCHA_STRINGS_ES,
});

export const SUPPORTED_LOCALES = Object.freeze(Object.keys(BUNDLES));

/**
 * Register one or more captcha-string bundles on a react-i18next instance.
 *
 * @param {object} i18nInstance - A react-i18next instance (the one the app mounts
 *   in `<I18nextProvider>`; NEVER the default singleton).
 * @param {string | string[]} [locales='en'] - Locale code(s): `'en'`, `'es'`, or
 *   any subset as an array.
 * @returns {object} The same `i18nInstance`, for chaining.
 * @throws {Error} If the instance has no `addResourceBundle()`, or a requested
 *   locale is not supported.
 */
// 2026-08-19: the canonical consumer-t → English-default fallback (AAS-I18N-001 close-out).
export { resolveString } from './resolveString.js';

export function loadCaptchaStrings(i18nInstance, locales = 'en') {
  if (!i18nInstance || typeof i18nInstance.addResourceBundle !== 'function') {
    throw new Error(
      'loadCaptchaStrings: expected a react-i18next instance with addResourceBundle()',
    );
  }
  const list = Array.isArray(locales) ? locales : [locales];
  list.forEach((locale) => {
    const bundle = BUNDLES[locale];
    if (!bundle) {
      throw new Error(
        `loadCaptchaStrings: unsupported locale "${locale}". Supported: ${SUPPORTED_LOCALES.join(', ')}`,
      );
    }
    // deep-merge = true, overwrite = false — an app's own override always wins.
    i18nInstance.addResourceBundle(locale, CAPTCHA_NAMESPACE, bundle, true, false);
  });
  return i18nInstance;
}

/*
 * 🗑️ `ensureCaptchaStrings(i18nInstance)` lived here until 2026-08-22 and was DELETED by
 * I18N-RENDER-002 (ruling R-2), together with the render-phase call in `useCaptchaTranslate` that
 * was its only consumer.
 *
 * It idempotently registered every supported locale's bundle onto whatever instance the React tree
 * happened to hold, short-circuiting on `hasResourceBundle` and never overwriting. Render-phase was
 * deliberate and correct at the time: react-i18next's `useTranslation` binds `languageChanged` and
 * does NOT re-render on an `added` resource event, so registering from a `useEffect` would have left
 * the FIRST paint English until the user changed language — a defect indistinguishable from the one
 * it was fixing.
 *
 * What made it necessary was hand-wiring: the sibling loaders were mounted in 20 `createAppI18n`
 * call sites, and `<CaptchaField>` renders far outside login flows, so an app could mount it without
 * ever thinking about login-shell. `@puffin-core/i18n-namespaces` now registers `captcha` for every
 * app from BOTH registries, inside `createAppI18n` immediately after `init()` — before first paint.
 * A second registrar behind it is not a safety net, it is a second mechanism, which is the thing
 * this initiative exists to delete.
 *
 * `loadCaptchaStrings` above is the surviving registrar and the one the registry calls. Pruned
 * rather than warehoused (RWC wire-or-prune), and pruned because it is genuinely superseded — not
 * because it was the cheap side.
 */
